// fb-tracking.js - Initialize on app load

class FacebookTracker {
    constructor() {
        this.data = this.initialize();
    }

    initialize() {
        if (typeof window === "undefined") {
            return {
                fbp: null,
                fbc: null,
                fbclid: null,
            };
        }

        const params = new URLSearchParams(window.location.search);

        // Get from URL (passed from landing page)
        const fbclid = params.get("fbclid");
        const fbpFromUrl = params.get("_fbp");
        const fbcFromUrl = params.get("_fbc");

        // Get from cookies (if same domain)
        const fbpCookie = this.getCookie("_fbp");
        const fbcCookie = this.getCookie("_fbc");

        // Construct fbc if we have fbclid but no fbc
        let fbc = fbcFromUrl || fbcCookie;
        if (!fbc && fbclid) {
            fbc = `fb.1.${Date.now()}.${fbclid}`;
        }

        const data = {
            fbp: fbpFromUrl || fbpCookie,
            fbc: fbc,
            fbclid: fbclid,
        };

        // Store in first-party cookies for persistence
        if (data.fbp) this.setCookie("_fbp", data.fbp, 90);
        if (data.fbc) this.setCookie("_fbc", data.fbc, 90);
        if (data.fbclid) this.setCookie("_fbclid", data.fbclid, 90);

        // Also localStorage as backup
        try {
            localStorage.setItem("fb_tracking", JSON.stringify(data));
        } catch (e) {
            // localStorage might not be available
        }

        return data;
    }

    getCookie(name) {
        if (typeof document === "undefined") return null;
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(";").shift();
        return null;
    }

    setCookie(name, value, days) {
        if (typeof document === "undefined") return;
        const expires = new Date(Date.now() + days * 864e5).toUTCString();
        document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax`;
    }

    cleanUrl() {
        if (typeof window === "undefined") return;
        try {
            const url = new URL(window.location.href);
            url.searchParams.delete("fbclid");
            url.searchParams.delete("_fbp");
            url.searchParams.delete("_fbc");
            window.history.replaceState({}, "", url.toString());
        } catch (e) {
            // URL parsing might fail in some environments
        }
    }

    // Call this when user signs up or converts
    getTrackingData() {
        if (typeof window === "undefined") {
            return {
                fbp: null,
                fbc: null,
                fbclid: null,
            };
        }

        // Prefer fresh cookies, fallback to stored data
        const freshFbp = this.getCookie("_fbp");
        const freshFbc = this.getCookie("_fbc");
        const freshFbclid = this.getCookie("_fbclid");

        // Try to get from localStorage as backup
        let storedData = null;
        try {
            const stored = localStorage.getItem("fb_tracking");
            if (stored) {
                storedData = JSON.parse(stored);
            }
        } catch (e) {
            // localStorage might not be available or parseable
        }

        return {
            fbp: freshFbp || storedData?.fbp || this.data?.fbp,
            fbc: freshFbc || storedData?.fbc || this.data?.fbc,
            fbclid: freshFbclid || storedData?.fbclid || this.data?.fbclid,
        };
    }

    // Get Facebook Pixel cookies in the format expected by the API
    getFacebookPixelCookies() {
        const trackingData = this.getTrackingData();
        return {
            fbpCookie: trackingData.fbp || undefined,
            fbcCookie: trackingData.fbc || undefined,
        };
    }
}

// Initialize globally if in browser
let fbTracker;
if (typeof window !== "undefined") {
    fbTracker = new FacebookTracker();
    window.fbTracker = fbTracker;
} else {
    // Server-side fallback
    fbTracker = {
        getTrackingData: () => ({
            fbp: null,
            fbc: null,
            fbclid: null,
        }),
        getFacebookPixelCookies: () => ({
            fbpCookie: undefined,
            fbcCookie: undefined,
        }),
    };
}

export default fbTracker;
